"use client";

import React, { useEffect, useRef, useState } from "react";
import { Mail, Phone, MapPin, Loader2, CheckCircle2 } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.1,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const inputClasses = "flex h-10 w-full rounded-md border border-foreground/20 bg-background px-3 py-2 text-base shadow-none transition-all duration-400 outline-none focus-visible:border-secondary focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm placeholder:text-muted-foreground aria-invalid:border-destructive";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="222340" className="flex flex-col items-center justify-center p-8 text-center bg-muted/20 rounded-lg border border-border">
        <CheckCircle2 className="size-16 text-secondary mb-4" />
        <h3 className="text-xl font-default font-semibold text-foreground mb-2">
          Mensagem Recebida!
        </h3>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="rounded-md border-foreground/20">
          Enviar nova mensagem
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "O nome é obrigatório"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-foreground">Nome Completo</FormLabel>
              <FormControl>
                <input {...field} type="text" className={inputClasses} placeholder="Seu nome completo" />
              </FormControl>
              <FormDescription className="text-xs">Informe seu nome para identificação.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "O e-mail é obrigatório",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "E-mail inválido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-foreground">E-mail Profissional</FormLabel>
              <FormControl>
                <input {...field} type="email" className={inputClasses} placeholder="exemplo@empresa.com" />
              </FormControl>
              <FormDescription className="text-xs">
                Responderemos através deste endereço.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        pattern: {
          value: /^[0-9\-\+\s\(\)]*$/,
          message: "Telefone inválido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-foreground">Telefone</FormLabel>
              <FormControl>
                <input {...field} type="tel" className={inputClasses} placeholder="(00) 00000-0000" />
              </FormControl>
              <FormDescription className="text-xs">
                Opcional para contato direto.
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" rules={{
        required: "Selecione um assunto"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold text-foreground">Assunto</FormLabel>
              <FormControl>
                <select {...field} className={inputClasses}>
                  <option value="" disabled>
                    Selecione o motivo
                  </option>
                  <option value="Consultoria">Consultoria Estratégica</option>
                  <option value="Projetos">Novos Projetos</option>
                  <option value="Suporte">Suporte ao Cliente</option>
                  <option value="Parcerias">Parcerias Comerciais</option>
                  <option value="Outros">Outros Assuntos</option>
                </select>
              </FormControl>
              <FormDescription className="text-xs">Como podemos ajudar hoje?</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" rules={{
      required: "A mensagem é obrigatória",
      minLength: {
        value: 10,
        message: "Mínimo de 10 caracteres"
      },
      maxLength: {
        value: 1000,
        message: "Máximo de 1000 caracteres"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default font-semibold text-foreground">Mensagem</FormLabel>
            <FormControl>
              <Textarea {...field} placeholder="Descreva sua solicitação detalhadamente..." className="bg-background border-foreground/20 min-h-[150px] resize-y rounded-md shadow-none transition-all duration-400 focus-visible:border-secondary focus-visible:ring-0" />
            </FormControl>
            <FormDescription className="text-xs">
              Forneça o máximo de detalhes possível.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full sm:w-auto px-10 py-6 text-base bg-secondary hover:bg-secondary/90 text-secondary-foreground rounded-md shadow-none transition-all duration-300 font-default font-bold">
        {isSubmitting ? <>
            <Loader2 className="mr-2 size-5 animate-spin" />
            Processando...
          </> : "Enviar Solicitação"}
      </Button>
    </Form>;
}
export default function FaleConosco() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const contactDetails = [{
    icon: Mail,
    title: "E-mail",
    value: "contato@empresa.com.br",
    linkTo: "mailto:contato@empresa.com.br",
    isExternal: true
  }, {
    icon: MapPin,
    title: "Escritório",
    value: "Av. Paulista, 1000 - Bela Vista, São Paulo - SP",
    linkTo: null,
    isExternal: false
  }];
  return;
}